import type { ValueOf } from "type-fest";

import { logger } from "~/libs/logger";

/**
 * Helper class for managing localStorage operations.
 */
export default class StorageHelper {
  /**
   * Storage keys used within the application.
   */
  static KEY = {
    LOGGER_CONFIG: "LOGGER_CONFIG",
  };

  /**
   * Sets an item in localStorage.
   * @param {string} key - The key under which the value is stored.
   * @param {any} value - The value to be stored. It will be serialized to JSON.
   */
  static setItem(key: ValueOf<typeof StorageHelper.KEY>, value: any): void {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      logger.error(
        "Failed to set localStorage item",
        {
          key,
          error: error instanceof Error ? error.message : "Unknown error",
          valueType: typeof value,
        },
        {
          component: "StorageHelper",
          file: "_storage.ts",
          function: "setItem",
        }
      );
    }
  }

  /**
   * Retrieves an item from localStorage.
   * @param {string} key - The key of the item to retrieve.
   * @returns {any} The deserialized value from localStorage, or null if not found.
   */
  static getItem(key: ValueOf<typeof StorageHelper.KEY>): any {
    try {
      const serializedValue = localStorage.getItem(key);
      return serializedValue ? JSON.parse(serializedValue) : null;
    } catch (error) {
      logger.error(
        "Failed to get localStorage item",
        {
          key,
          error: error instanceof Error ? error.message : "Unknown error",
        },
        {
          component: "StorageHelper",
          file: "_storage.ts",
          function: "getItem",
        }
      );
      return null;
    }
  }

  /**
   * Removes an item from localStorage.
   * @param {string} key - The key of the item to remove.
   */
  static removeItem(key: ValueOf<typeof StorageHelper.KEY>): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error(
        "Failed to remove localStorage item",
        {
          key,
          error: error instanceof Error ? error.message : "Unknown error",
        },
        {
          component: "StorageHelper",
          file: "_storage.ts",
          function: "removeItem",
        }
      );
    }
  }

  /**
   * Clears all items from localStorage.
   */
  static clearStorage(): void {
    try {
      localStorage.clear();
    } catch (error) {
      logger.error(
        "Failed to clear localStorage",
        {
          error: error instanceof Error ? error.message : "Unknown error",
        },
        {
          component: "StorageHelper",
          file: "_storage.ts",
          function: "clearStorage",
        }
      );
    }
  }
}
